import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, FileText, PencilLine } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import IntakeQuoteSelect from "../components/intake/IntakeQuoteSelect";
import IntakeClientStep from "../components/intake/IntakeClientStep";
import IntakeProjectStep from "../components/intake/IntakeProjectStep";
import IntakeScopeStep from "../components/intake/IntakeScopeStep";
import IntakeReviewStep from "../components/intake/IntakeReviewStep";
import IntakeSubmittedStep from "../components/intake/IntakeSubmittedStep";
import { buildQuotePrefill, prefillAccountsPayable, derivePayment } from "../components/intake/quoteMapping";

const quoteContractOneTime = (quote) => derivePayment(quote).oneTime;

function detectTimezone() {
  const TIMEZONES = [
    "America/New_York", "America/Chicago", "America/Denver", 
    "America/Los_Angeles", "America/Phoenix", "America/Anchorage",
    "Pacific/Honolulu", "America/Toronto", "Europe/London"
  ];
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TIMEZONES.includes(tz) ? tz : "America/New_York";
}

function formatPhone(phone) {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return phone;
}

function isValidUrl(string) {
  try { new URL(string); return true; } 
  catch (_) { return false; }
}

function parseLinks(text) {
  return text.split('\n').map(l => l.trim()).filter(l => l && isValidUrl(l));
}

// A fresh wizard state. Lives outside the component so "Start another intake"
// on the completion screen can reset back to exactly the mount-time defaults.
function blankForm() {
  return {
    client_name: "",
    client_entity: "", // NEW: Company / LLC (pulled from quote in quote mode)
    client_email: "",
    client_phone: "",
    client_timezone: detectTimezone(),
    external_contacts: [], // NEW: Additional contacts
    // Who we invoice. Every payment notice routes here; required to activate.
    accounts_payable: { name: "", email: "", title: "", phone: "" },
    source_quote_id: null, // NEW: originating Client-Calculator quote
    project_name: "",
    project_city: "",
    project_state: "",
    project_address_full: "",
    service_type: "Renderings",
    service_summary: "", // NEW: human scope descriptor (quote mode)
    start_date: "",
    expected_delivery_date: "",
    delivery_weeks: 8,
    vertical_safe_zone_required: false,
    additional_services: [],
    scope_details: "",
    assigned_pm_id: "", // NEW: PM selection during intake
    // Agreement - support link OR upload
    signed_agreement_link: "",
    agreement_type: "link",
    agreement_file: null,
    // Other links
    fathom_links_text: "",
    project_file_links: [], // NEW: Structured project file links [{title, url}]
    project_breakdown_video_link: "",
    reference_links_text: "",
    // Payment - updated fields
    payment_method: "Wire",
    contract_value: "",
    expected_deposit: "",
    payment_terms: "",
    internal_notes: ""
  };
}

export default function IntakeForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [pmUsers, setPmUsers] = useState([]);
  // Quote bridge: gate the wizard behind a "pick a quote or go manual" choice.
  const [intakeMode, setIntakeMode] = useState(null); // null | "quote" | "manual"
  const [sourceQuote, setSourceQuote] = useState(null);
  const [selectingId, setSelectingId] = useState(null);
  const quoteMode = intakeMode === "quote";
  // Set on a successful submit — swaps the wizard for the completion screen
  // (which carries the ops-briefing booking button) instead of redirecting.
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState(blankForm);
  const [errors, setErrors] = useState({});

  // Back to a clean wizard after a submit ("Start another intake").
  function handleNewIntake() {
    setSubmitted(null);
    setForm(blankForm());
    setSourceQuote(null);
    setIntakeMode(null);
    setCurrentStep(1);
    setErrors({});
  }

  // Fetch the full quote, prefill the form, and enter quote-driven mode.
  async function handleSelectQuote(quote) {
    setSelectingId(quote.id);
    try {
      let full = quote;
      try {
        const res = await apiClient.get(`/vexos-quotes/${quote.id}`);
        full = res.data || quote;
      } catch (_) {
        // Fall back to the list record if the detail fetch fails.
      }
      const prefill = buildQuotePrefill(full);
      setForm(prev => ({
        ...prev,
        ...prefill,
        // Switching to a quote with no DocuSign envelope drops the DocuSign choice.
        agreement_type: prefill.agreement_type
          || (prev.agreement_type === "docusign" ? "link" : prev.agreement_type),
        accounts_payable: prefillAccountsPayable(prev.accounts_payable, sourceQuote, full),
      }));
      setSourceQuote(full);
      setIntakeMode("quote");
      setCurrentStep(1);
      setErrors({});
    } finally {
      setSelectingId(null);
    }
  }

  function handleManual() {
    setSourceQuote(null);
    setForm(prev => ({
      ...prev,
      source_quote_id: null,
      service_summary: "",
      // No quote, no DocuSign envelope to pull from.
      agreement_type: prev.agreement_type === "docusign" ? "link" : prev.agreement_type,
    }));
    setIntakeMode("manual");
    setCurrentStep(1);
    setErrors({});
  }

  function validateStep(step) {
    const newErrors = {};
    
    if (step === 1) {
      if (!form.client_name.trim()) newErrors.client_name = "Client name is required";
      if (!form.client_email.trim()) newErrors.client_email = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.client_email)) newErrors.client_email = "Invalid email format";
      if (!form.client_phone.trim()) newErrors.client_phone = "Phone is required";

      // A contact row is dropped from the payload unless it has BOTH a name
      // and an email. A completely untouched row is fine — that's a stray
      // "Add Contact" click — but a half-filled one is data the rep can see on
      // screen that would vanish on submit with nothing said about it.
      (form.external_contacts || []).forEach((c, idx) => {
        const name = (c.name || "").trim();
        const email = (c.email || "").trim();
        const role = (c.role || "").trim();
        if (!name && !email && !role) return; // untouched row, discarded quietly
        const key = `external_contact_${idx}`;
        if (!name) newErrors[key] = "Add a name, or remove this contact";
        else if (!email) newErrors[key] = "Add an email, or remove this contact";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors[key] = "Invalid email format";
      });

      // The AP contact is required here rather than only at activation: the rep
      // is on the phone with the client now, and Ops chasing it later is the
      // whole reason payment notices used to land on the wrong desk.
      const ap = form.accounts_payable || {};
      const apName = (ap.name || "").trim();
      const apEmail = (ap.email || "").trim();
      if (!apName) newErrors.ap_name = "Who do we invoice?";
      if (!apEmail) newErrors.ap_email = "An AP email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(apEmail)) {
        newErrors.ap_email = "Invalid email format";
      }
    }
    
    if (step === 2) {
      if (!form.project_name.trim()) newErrors.project_name = "Project name is required";
      if (!form.project_city.trim()) newErrors.project_city = "City is required";
      if (!form.project_state.trim()) newErrors.project_state = "State is required";
      if (!form.start_date) newErrors.start_date = "Start date is required";
      if (!form.expected_delivery_date) newErrors.expected_delivery_date = "Delivery date is required";
      if (form.start_date && form.expected_delivery_date && form.start_date > form.expected_delivery_date) {
        newErrors.expected_delivery_date = "Delivery date must be after start date";
      }
    }
    
    if (step === 3) {
      // Agreement validation - either link OR file required
      // "docusign": the backend pulls the signed PDF from the quote's envelope.
      if (form.agreement_type === "link") {
        if (!form.signed_agreement_link.trim()) newErrors.signed_agreement_link = "Agreement link is required";
        else if (!isValidUrl(form.signed_agreement_link)) newErrors.signed_agreement_link = "Invalid URL format";
      } else if (form.agreement_type === "upload") {
        if (!form.agreement_file) newErrors.signed_agreement_link = "Please upload an agreement file";
      }
      
      // Payment validation
      if (form.expected_deposit && form.contract_value) {
        if (parseFloat(form.expected_deposit) > parseFloat(form.contract_value)) {
          newErrors.expected_deposit = "Deposit cannot exceed contract value";
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleNext() {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  }

  function handleBack() {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }

  async function handleSubmit() {
    for (let step = 1; step <= 3; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step);
        toast.error("Please fix the errors before submitting");
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      let agreementFileId = null;
      
      // If file was uploaded, upload it first
      if (form.agreement_type === "upload" && form.agreement_file) {
        const formData = new FormData();
        formData.append("file", form.agreement_file);
        
        try {
          const uploadRes = await apiClient.post("/intake/upload-agreement", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          agreementFileId = uploadRes.data.file_id;
        } catch (uploadErr) {
          toast.error("Failed to upload agreement file");
          setSubmitting(false);
          return;
        }
      }
      
      const payload = {
        client_name: form.client_name.trim(),
        client_entity: form.client_entity?.trim() || null, // NEW: Company / LLC
        client_email: form.client_email.trim().toLowerCase(),
        client_phone: formatPhone(form.client_phone),
        client_timezone: form.client_timezone,
        // Validation above guarantees every surviving row is complete, so this
        // only sheds untouched rows. Trim first: " " is truthy, so a
        // whitespace-only row used to sail through as a real contact.
        external_contacts: (form.external_contacts || [])
          .map(c => ({
            name: (c.name || "").trim(),
            email: (c.email || "").trim().toLowerCase(),
            role: (c.role || "").trim() || null
          }))
          .filter(c => c.name && c.email),
        // Trimmed and lower-cased like every other address here. Sent as null
        // rather than a hollow object when empty, so the backend's "is there an
        // AP contact" check can't be satisfied by blank strings.
        accounts_payable: (() => {
          const ap = form.accounts_payable || {};
          const name = (ap.name || "").trim();
          const email = (ap.email || "").trim().toLowerCase();
          if (!name || !email) return null;
          return {
            name,
            email,
            title: (ap.title || "").trim() || null,
            phone: (ap.phone || "").trim() || null,
          };
        })(),
        source_quote_id: form.source_quote_id || null, // NEW: originating quote
        project_name: form.project_name.trim(),
        project_city: form.project_city.trim(),
        project_state: form.project_state.trim().toUpperCase(),
        project_address_full: form.project_address_full?.trim() || null,
        service_type: form.service_type,
        service_summary: form.service_summary?.trim() || null, // NEW: scope descriptor
        start_date: form.start_date,
        expected_delivery_date: form.expected_delivery_date,
        delivery_weeks: form.delivery_weeks,
        vertical_safe_zone_required: form.vertical_safe_zone_required,
        additional_services: form.additional_services,
        scope_details: form.scope_details.trim() || null,
        assigned_pm_id: form.assigned_pm_id || null, // NEW
        // Agreement - support link OR upload reference
        signed_agreement_link: form.agreement_type === "link" ? form.signed_agreement_link.trim() : null,
        signed_agreement_file_id: agreementFileId,
        agreement_type: form.agreement_type,
        fathom_links: parseLinks(form.fathom_links_text),
        project_file_links: form.project_file_links || [], // NEW: Structured project file links
        project_breakdown_video_link: form.project_breakdown_video_link.trim() || null,
        reference_links: parseLinks(form.reference_links_text),
        // Payment - updated fields
        payment_method: form.payment_method,
        contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
        // Quote mode: the one-time half of contract_value (the rest is the
        // subscription term). Manual intakes have no subscription — omitted.
        one_time_contract_value: quoteMode && sourceQuote ? quoteContractOneTime(sourceQuote) : null,
        expected_deposit: form.expected_deposit ? parseFloat(form.expected_deposit) : null,
        payment_terms: form.payment_terms.trim() || null,
        internal_notes: form.internal_notes.trim() || null
      };
      
      const response = await apiClient.post("/intake/submissions", payload);
      
      // Land on the completion screen rather than redirecting — it carries the
      // book-your-ops-briefing button, which is the rep's actual next step.
      setSubmitted(response.data);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit intake");
    } finally {
      setSubmitting(false);
    }
  }

  const progress = (currentStep / totalSteps) * 100;
  const stepLabels = ["Client", "Project", "Links & Scope", "Review"];

  return (
    <div data-testid="intake-form" className="max-w-3xl mx-auto pb-20 md:pb-0">
      <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="flex-shrink-0">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl md:text-3xl font-light tracking-tight truncate" style={{ fontFamily: 'var(--font-sans)' }}>
            New Intake
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground truncate">
            {user?.name} ({user?.role})
          </p>
        </div>
      </div>

      {submitted ? (
        <IntakeSubmittedStep result={submitted} onNewIntake={handleNewIntake} />
      ) : intakeMode === null ? (
        <IntakeQuoteSelect
          onSelect={handleSelectQuote}
          onManual={handleManual}
          loadingSelection={selectingId !== null}
          selectingId={selectingId}
        />
      ) : (
        <>
          <div className="mb-6 sm:mb-8">
            <div className="flex justify-between text-xs sm:text-sm text-muted-foreground mb-2">
              <span>Step {currentStep} of {totalSteps}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-1.5 sm:h-2" />
            <div className="flex justify-between mt-2">
              {stepLabels.map((label, idx) => (
                <span
                  key={label}
                  className={`text-[10px] sm:text-xs ${currentStep > idx + 1 ? "text-green-600" : currentStep === idx + 1 ? "text-primary font-medium" : "text-muted-foreground"}`}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Source banner — shows which quote this intake is sourced from (or manual). */}
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border bg-secondary/30 px-3 py-2">
            {quoteMode ? (
              <div className="flex items-center gap-2 min-w-0 text-sm">
                <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-muted-foreground">Sourced from quote:</span>
                <span className="font-medium truncate">{sourceQuote?.client_name}</span>
                {sourceQuote?.created_by_name && (
                  <Badge variant="secondary" className="text-[10px]">{sourceQuote.created_by_name}</Badge>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <PencilLine className="w-4 h-4 flex-shrink-0" />
                Manual intake (no quote linked)
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs flex-shrink-0"
              onClick={() => { setIntakeMode(null); setCurrentStep(1); }}
            >
              Change
            </Button>
          </div>

          {currentStep === 1 && (
            <IntakeClientStep form={form} setForm={setForm} errors={errors} onNext={handleNext} quoteMode={quoteMode} />
          )}
          {currentStep === 2 && (
            <IntakeProjectStep form={form} setForm={setForm} errors={errors} onNext={handleNext} onBack={handleBack} quoteMode={quoteMode} sourceQuote={sourceQuote} />
          )}
          {currentStep === 3 && (
            <IntakeScopeStep form={form} setForm={setForm} errors={errors} onNext={handleNext} onBack={handleBack} quoteMode={quoteMode} sourceQuote={sourceQuote} />
          )}
          {currentStep === 4 && (
            <IntakeReviewStep form={form} onBack={handleBack} onSubmit={handleSubmit} submitting={submitting} quoteMode={quoteMode} sourceQuote={sourceQuote} />
          )}
        </>
      )}
    </div>
  );
}
